/**
 * Static project shape — the phase ledger, gates and calendar from the engagement's
 * PHASES.md. Live status lives in Firestore and is merged over this at read time; this
 * file is the baseline that does not change without a change order.
 */

export const PROJECT = {
  client: 'Missional Marketing',
  deliveredBy: 'Milestone Consulting',
  goLive: '2026-10-09',
  hypercareEnds: '2026-10-27',
  baselineHours: 880,
  rate: 82.5,
  baselineFee: 72600,
  driveFolderUrl: 'https://drive.google.com/drive/folders/1QTU-0gSa5WCWbnOogrFHp5AeTO8e5H8A',
  packetsFolderUrl: 'https://drive.google.com/drive/folders/1NnG6bK0y-O5FCV_lw-MbSUBDS90lerth',
  registerUrl:
    'https://docs.google.com/spreadsheets/d/10K4UzMpW0t8DVfFEdN8vScV2wRoxboKQkpFyWVbWPUM/edit',
} as const

export const CONTRACT = {
  title: 'Salesforce Migration — Statement of Work',
  url: 'https://drive.google.com/file/d/1hEe0aBMeiTJ0Ds7RWRzgJbSun_bqZZRi/view',
  signedBy: [
    { name: 'Andy Haas', org: 'Milestone Consulting', date: '2026-08-24' },
    { name: 'Kevin Peck', org: 'Missional Marketing', date: '2026-08-26' },
  ],
  basis: 'Time and materials',
  rate: 82.5,
  baselineHours: 880,
  note: 'The 880 hours are a planning estimate, not a cap and not a fixed fee. Invoices reconcile to actual time worked.',
} as const

export type Category =
  | 'Foundation' | 'CRM & Delivery' | 'Customer Experience'
  | 'Data Migration' | 'Integrations' | 'UAT & Launch'

export type Phase = {
  id: string
  name: string
  category: Category
  hours: number
  week: string
}

/** Net Milestone hours after client-owned reductions. Total 880. */
export const PHASES: Phase[] = [
  { id: '0',     name: 'Mobilization and scope baseline',                   category: 'Foundation',          hours: 24,  week: 'W1' },
  { id: '1',     name: 'Pre-data architecture and dependency mapping',      category: 'Foundation',          hours: 80,  week: 'W1' },
  { id: '2',     name: 'Client validation sheets and one-passes',           category: 'Foundation',          hours: 50,  week: 'W2' },
  { id: '3',     name: 'New-org foundation, security, DevOps',              category: 'Foundation',          hours: 35,  week: 'W2' },
  { id: '4A',    name: 'Core Sales/Service config, Flow, Apex, LWC',        category: 'CRM & Delivery',      hours: 100, week: 'W3' },
  { id: '4B',    name: 'Project and product-linked Work Item automation',   category: 'CRM & Delivery',      hours: 55,  week: 'W3' },
  { id: '4C',    name: 'Customer Portal — React frontend + SF backend',     category: 'Customer Experience', hours: 100, week: 'W4' },
  { id: '4D',    name: 'Agency Calculator — Opportunity LWC',               category: 'CRM & Delivery',      hours: 38,  week: 'W4' },
  { id: 'CX',    name: 'Customer/Partner/Coach experience framework',       category: 'Customer Experience', hours: 0,   week: 'W4' },
  { id: '5',     name: 'Data migration execution and reconciliation',       category: 'Data Migration',      hours: 40,  week: 'W3' },
  { id: '5A',    name: 'Green Light Case-to-Project migration',             category: 'Data Migration',      hours: 30,  week: 'W5' },
  { id: '5B',    name: 'Current customer Order and Subscription init',      category: 'Data Migration',      hours: 25,  week: 'W5' },
  { id: '6A',    name: 'Time Entry core and Project retargeting',           category: 'Integrations',        hours: 45,  week: 'W4' },
  { id: '6B-6C', name: 'Rippling + Upwork controlled lift-and-shift',       category: 'Integrations',        hours: 5,   week: 'W4' },
  { id: '6D',    name: 'PandaDoc platform and ~4 Agency templates',         category: 'Integrations',        hours: 15,  week: 'W4' },
  { id: '6E',    name: 'Calendly integration',                              category: 'Integrations',        hours: 8,   week: 'W5' },
  { id: '6F',    name: 'Stripe billing, payments, platform configuration',  category: 'Integrations',        hours: 70,  week: 'W3' },
  { id: '6G',    name: 'Vendasta Tool Kit Salesforce handoff',              category: 'Integrations',        hours: 8,   week: 'W5' },
  { id: '6H',    name: 'Vendasta purchase-to-Salesforce automation',        category: 'Integrations',        hours: 30,  week: 'W4' },
  { id: '6I',    name: 'Agency e-sign to payment and delivery orchestration', category: 'Integrations',      hours: 15,  week: 'W4' },
  { id: '7',     name: 'Program management, QA, and governance',            category: 'Foundation',          hours: 45,  week: 'W1' },
  { id: '7A',    name: 'Einstein Activity Capture setup and validation',    category: 'Integrations',        hours: 2,   week: 'Post' },
  { id: '8',     name: 'UAT, training, cutover, and hypercare',             category: 'UAT & Launch',        hours: 60,  week: 'W6' },
]

export type GateStatus = 'Open' | 'At Risk' | 'Cleared'

export type Gate = {
  id: string
  name: string
  owner: 'Client' | 'Milestone'
  target: string
  clearsWhen: string
  blocks: string
}

export const GATES: Gate[] = [
  { id: 'G1', name: 'Mobilization complete', owner: 'Client', target: '2026-09-04',
    clearsWhen: 'Named owners on all roles, access plan executed, scope baseline confirmed, RAID open',
    blocks: 'Phase 1' },
  { id: 'G2', name: 'Architecture approved', owner: 'Milestone', target: '2026-09-18',
    clearsWhen: 'Target object model, source-to-target relationships, automation/integration dependencies, sequencing signed off',
    blocks: 'Phases 4A, 4B, 5' },
  { id: 'G3', name: 'Decision sheets one-passed', owner: 'Client', target: '2026-09-18',
    clearsWhen: 'Object-specific workbooks approved by named client owner; mappings locked',
    blocks: 'Phase 4A build lock, Phase 5 mapping' },
  { id: 'G4', name: 'Design baseline approved', owner: 'Client', target: '2026-09-18',
    clearsWhen: "Sarah's Customer framework and Chuck's Calculator framework meet the implementation-readiness gate",
    blocks: 'Phases 4C, 4D frontend build' },
  { id: 'G5', name: 'Calculation spec approved', owner: 'Client', target: '2026-10-02',
    clearsWhen: 'Client-documented formulas, inputs, outputs, rounding, discounts, overrides, validation, expected results',
    blocks: 'Phase 4D implementation' },
  { id: 'G6', name: 'Rehearsal reconciled', owner: 'Milestone', target: '2026-10-02',
    clearsWhen: 'Rehearsal import verified, counts and relationships reconciled, corrections reported and accepted',
    blocks: 'Cutover' },
  { id: 'G7', name: 'Go / No-Go', owner: 'Milestone', target: '2026-10-08',
    clearsWhen: 'UAT accepted, training delivered, negative-access tests passed, rollback prepared, integrations verified',
    blocks: 'Production cutover' },
]

export type Week = {
  id: string
  dates: string
  start: string
  end: string
  focus: string
  gatePressure: string
  gates: string[]
}

export const CALENDAR: Week[] = [
  { id: 'W1', dates: 'Sep 1 – Sep 4', start: '2026-09-01', end: '2026-09-04',
    focus: 'Phase 0 mobilization; Phase 1 architecture starts; provisioning',
    gatePressure: 'Owners named, org/licenses provisioned, access granted', gates: [] },
  { id: 'W2', dates: 'Sep 7 – Sep 11', start: '2026-09-07', end: '2026-09-11',
    focus: 'Phase 1 continues; Phase 2 decision sheets issued; Phase 3 new-org foundation',
    gatePressure: 'DKIM DNS published, SKU mappings supplied', gates: ['G1'] },
  { id: 'W3', dates: 'Sep 14 – Sep 18', start: '2026-09-14', end: '2026-09-18',
    focus: 'Phase 4A/4B build; Phase 6F Stripe; Phase 5 SOQL exports issued',
    gatePressure: 'Sarah + Chuck design baselines due', gates: ['G2', 'G3', 'G4'] },
  { id: 'W4', dates: 'Sep 21 – Sep 25', start: '2026-09-21', end: '2026-09-25',
    focus: 'Phase 4C portal; 4D Calculator; 6A/6B-6C time entry; 6D/6H/6I; rehearsal #1',
    gatePressure: 'Client transformation workbooks returned', gates: [] },
  { id: 'W5', dates: 'Sep 28 – Oct 2', start: '2026-09-28', end: '2026-10-02',
    focus: 'Rehearsal #2; 5A/5B; 6E Calendly; 6G Vendasta handoff; QA; UAT starts',
    gatePressure: 'Calculation spec approved, rehearsal reconciliation approvals, UAT participants available', gates: ['G5', 'G6'] },
  { id: 'W6', dates: 'Oct 5 – Oct 9', start: '2026-10-05', end: '2026-10-09',
    focus: 'Rehearsal #3, final QA, training, Go/No-Go, cutover, GO-LIVE Oct 9',
    gatePressure: 'Portal user roster + invitations, change windows approved', gates: ['G7'] },
  { id: 'Post', dates: 'Oct 12 – Oct 27', start: '2026-10-12', end: '2026-10-27',
    focus: 'Hypercare, monitoring, reconciliation, backfill, legacy comparison',
    gatePressure: 'Legacy access confirmed through Oct 27', gates: [] },
]

/** `YYYY-MM-DD` in the reader's own time zone, to compare against the calendar's dates. */
const localDay = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

/**
 * The week `on` falls in — the latest one that has started by then.
 *
 * Worked out from the date rather than stored, because a stored week is a number somebody
 * has to remember to change every Monday, and the first Monday nobody does it the page is
 * wrong. A weekend stays with the week just finished; before W1 reads as W1, and anything
 * after hypercare as Post.
 */
export function weekOn(on: Date = new Date()): Week {
  const day = localDay(on)
  return CALENDAR.findLast((w) => w.start <= day) ?? CALENDAR[0]!
}
